import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface RefundRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: {
    id: string;
    amount_cents: number;
    currency: string;
    created_at: string;
  } | null;
  onSuccess?: () => void;
}

const REFUND_REASONS = [
  { value: 'service_not_provided', label: 'Service was not provided' },
  { value: 'service_unsatisfactory', label: 'Service quality was unsatisfactory' },
  { value: 'incorrect_charge', label: 'Incorrect amount charged' },
  { value: 'duplicate_charge', label: 'Duplicate charge' },
  { value: 'agreed_cancellation', label: 'Both parties agreed to cancel' },
  { value: 'other', label: 'Other reason' },
];

export const RefundRequestDialog = ({ 
  open, 
  onOpenChange, 
  transaction,
  onSuccess 
}: RefundRequestDialogProps) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!user || !transaction || !reason) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('refund_requests')
        .insert({
          transaction_id: transaction.id,
          requester_id: user.id,
          amount_cents: transaction.amount_cents,
          reason,
          detailed_explanation: explanation || null,
        });

      if (error) throw error;

      toast.success('Refund request submitted', {
        description: 'An administrator will review your request. You can expect a decision in approximately 3 business days.'
      });
      
      onOpenChange(false);
      setReason('');
      setExplanation('');
      onSuccess?.();
    } catch (e: any) {
      console.error('Error submitting refund request:', e);
      toast.error('Failed to submit request', {
        description: e.message || 'Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request a Refund</DialogTitle>
          <DialogDescription>
            Submit a refund request for ${(transaction.amount_cents / 100).toFixed(2)} {transaction.currency.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                <strong>Please Note:</strong> Refund requests are reviewed by our team and are subject to our{' '}
                <a href="/terms-of-service" target="_blank" className="underline">Terms of Service</a>.
                Refunds are granted at the sole discretion of AnySkill administration. 
                You will be notified of the decision within 3 business days.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Refund *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="explanation">Additional Details</Label>
            <Textarea
              id="explanation"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Please provide any additional details that may help us review your request..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Include relevant details such as communication with the service provider, 
              what went wrong, and any attempts to resolve the issue directly.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !reason}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
